import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Briefcase, CalendarDays, CheckCircle2, Euro, FolderOpen, MessageCircle, Star } from 'lucide-react';
import { getOpleiderSignalen, markeerOpleiderSignaalGelezen } from '../services/opleidingenApi';
import type { OpleiderSignal, OpleiderSignalCategory, OpleiderSignalSeverity, OpleiderSignalenResponse } from '../services/opleidingenApi';
import { initSocket } from '../services/socketService';

/**
 * Opleiders-dashboard Menu 1 — het Actiecentrum & de Meldingenfeed.
 *
 * Traffic Director: twaalf signalen (boekingen, offertes, groepsinkoop, rooster,
 * wachtlijst, annuleringen, facturen, reviews, audit) in vier categorieën. Een
 * klik zet het signaal op gelezen en stuurt door naar de deeplink; afhandelen
 * gebeurt altijd op de doelpagina. Server: /api/opleider/dashboard/signalen.
 */

const CATEGORIEEN: Array<{ key: OpleiderSignalCategory; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'commercieel', icon: Briefcase },
  { key: 'operationeel', icon: CalendarDays },
  { key: 'financieel', icon: Euro },
  { key: 'kwaliteit', icon: Star },
];

const SEVERITY_STIJL: Record<OpleiderSignalSeverity, { rand: string; stip: string; icon: string }> = {
  urgent: { rand: 'border-l-red-500', stip: 'bg-red-500', icon: 'text-red-600' },
  actie: { rand: 'border-l-amber-400', stip: 'bg-amber-400', icon: 'text-amber-600' },
  info: { rand: 'border-l-blue-400', stip: 'bg-blue-400', icon: 'text-blue-600' },
};

export const OPLEIDER_SIGNALEN_QUERY_KEY = ['opleider-dashboard-signalen'];

const eur = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

export const OpleiderSignalenFeed: React.FC = () => {
  const { t, i18n } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<OpleiderSignalCategory | 'alle'>('alle');

  const { data, isLoading, isError } = useQuery<OpleiderSignalenResponse>({
    queryKey: OPLEIDER_SIGNALEN_QUERY_KEY,
    queryFn: getOpleiderSignalen,
    // Afgeleide signalen hebben geen socket-event; pollen houdt ze actueel.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: OPLEIDER_SIGNALEN_QUERY_KEY });
    socket.on('new_notification', ververs);
    socket.on('new_message', ververs);
    return () => {
      socket.off('new_notification', ververs);
      socket.off('new_message', ververs);
    };
  }, [queryClient]);

  const signalen = useMemo(() => data?.signalen ?? [], [data?.signalen]);
  const zichtbaar = useMemo(
    () => (filter === 'alle' ? signalen : signalen.filter((s) => s.category === filter)),
    [signalen, filter]
  );

  const taal = i18n.language?.startsWith('en') ? 'en' : 'nl';
  const tijdFormatter = useMemo(() => new Intl.RelativeTimeFormat(taal, { numeric: 'auto' }), [taal]);
  const relatieveTijd = (iso: string) => {
    const min = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
    if (Math.abs(min) < 60) return tijdFormatter.format(min, 'minute');
    const uur = Math.round(min / 60);
    if (Math.abs(uur) < 24) return tijdFormatter.format(uur, 'hour');
    return tijdFormatter.format(Math.round(uur / 24), 'day');
  };
  const datumKort = (iso: string) =>
    new Date(iso).toLocaleString(taal === 'en' ? 'en-GB' : 'nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const openSignaal = (s: OpleiderSignal) => {
    if (!s.isRead) {
      // Optimistisch op gelezen; het doorsturen wacht niet op de server.
      queryClient.setQueryData<OpleiderSignalenResponse>(OPLEIDER_SIGNALEN_QUERY_KEY, (oud) =>
        oud
          ? {
              ...oud,
              ongelezen: Math.max(0, oud.ongelezen - 1),
              signalen: oud.signalen.map((x) => (x.id === s.id ? { ...x, isRead: true } : x)),
            }
          : oud
      );
      markeerOpleiderSignaalGelezen(s.id).catch(() => queryClient.invalidateQueries({ queryKey: OPLEIDER_SIGNALEN_QUERY_KEY }));
    }
    navigate(s.deeplink);
  };

  /** Titel + tekst per signaal; varianten (kosteloos/bescherming, goed-/afgekeurd, gevaar) via params. */
  const signaalTekst = (s: OpleiderSignal): { titel: string; tekst: string; waarschuwing: string | null } => {
    if (s.source === 'notificatie') return { titel: s.title ?? '', tekst: s.content ?? '', waarschuwing: null };
    const p = { ...s.params } as Record<string, any>;
    for (const k of ['bedrag', 'netto']) if (typeof p[k] === 'number') p[k] = eur.format(p[k]);
    if (typeof p.datum === 'string') p.datum = datumKort(p.datum);
    if (typeof p.gemiddelde === 'number') p.gemiddelde = p.gemiddelde.toLocaleString(taal === 'en' ? 'en-GB' : 'nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    if (!p.medewerker) p.medewerker = t('opleiderDashboard.onbekendeMedewerker');
    if (!p.bedrijf) p.bedrijf = t('opleiderDashboard.onbekendBedrijf');
    if (!p.werkgever) p.werkgever = t('opleiderDashboard.onbekendBedrijf');
    if (!p.rij) p.rij = t('opleiderDashboard.geenRij');
    if (!p.toelichting) p.toelichting = '';

    const basis = `opleiderDashboard.signalen.${s.key}`;
    let titelKey = `${basis}.titel`;
    let tekstKey = `${basis}.tekst`;
    let waarschuwing: string | null = null;
    switch (s.key) {
      case 'SIGNAL_BOOKING_CANCELLED':
        tekstKey = p.kosteloos ? `${basis}.tekst_kosteloos` : `${basis}.tekst_bescherming`;
        break;
      case 'SIGNAL_INVOICE_PAID':
        tekstKey = p.uitbetaald ? `${basis}.tekst_uitbetaald` : `${basis}.tekst`;
        break;
      case 'SIGNAL_COURSE_AUDIT_RESULT':
        titelKey = p.status === 'REJECTED' ? `${basis}.titel_afgekeurd` : `${basis}.titel_goedgekeurd`;
        tekstKey = p.status === 'REJECTED' ? `${basis}.tekst_afgekeurd` : `${basis}.tekst_goedgekeurd`;
        break;
      case 'SIGNAL_NEW_REVIEW':
        if (p.gevaar) waarschuwing = String(t(`${basis}.gevaar`));
        break;
    }
    return { titel: String(t(titelKey, p)), tekst: String(t(tekstKey, p)), waarschuwing };
  };

  return (
    <section className="mb-8" aria-labelledby="opleider-feed-titel">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 id="opleider-feed-titel" className="font-semibold text-gray-900">{t('opleiderDashboard.title')}</h2>
          <p className="text-sm text-gray-500">{t('opleiderDashboard.subtitle')}</p>
        </div>
      </div>

      {/* Categorieën: tellers + filter. Navigatie-hulp, geen acties. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setFilter('alle')}
          className={`text-left rounded-lg border px-3 py-3 transition-colors ${
            filter === 'alle' ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <FolderOpen className="w-5 h-5 text-[#0B1F3A]" />
            {(data?.ongelezen ?? 0) > 0 && (
              <span className="text-xs font-semibold bg-[#E5A425] text-gray-900 rounded-full px-2 py-0.5">{data!.ongelezen}</span>
            )}
          </div>
          <div className="mt-2 text-sm font-medium text-gray-900">{t('opleiderDashboard.alle')}</div>
          <div className="text-xs text-gray-500">{t('opleiderDashboard.aantalSignalen', { count: signalen.length })}</div>
        </button>

        {CATEGORIEEN.map(({ key, icon: Icon }) => {
          const teller = data?.tellers?.[key] ?? { totaal: 0, ongelezen: 0 };
          const actief = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(actief ? 'alle' : key)}
              className={`text-left rounded-lg border px-3 py-3 transition-colors ${
                actief ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon className="w-5 h-5 text-[#0B1F3A]" />
                {teller.ongelezen > 0 && (
                  <span className="text-xs font-semibold bg-[#E5A425] text-gray-900 rounded-full px-2 py-0.5">{teller.ongelezen}</span>
                )}
              </div>
              <div className="mt-2 text-sm font-medium text-gray-900">{t(`opleiderDashboard.categorie.${key}`)}</div>
              <div className="text-xs text-gray-500">{t('opleiderDashboard.aantalSignalen', { count: teller.totaal })}</div>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-gray-200 animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t('opleiderDashboard.fout')}
        </div>
      ) : zichtbaar.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">{t('opleiderDashboard.leegTitel')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('opleiderDashboard.leegTekst')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {zichtbaar.map((s) => {
            const stijl = SEVERITY_STIJL[s.severity];
            const Icon = CATEGORIEEN.find((c) => c.key === s.category)?.icon ?? FolderOpen;
            const { titel, tekst, waarschuwing } = signaalTekst(s);
            const actie = s.source === 'afgeleid' ? t(`opleiderDashboard.acties.${s.key}`, { defaultValue: '' }) : '';
            // Menu 5 Tab B: thread met de HR-contactpersoon van de offerte-aanvraag
            const chatMet = s.key === 'SIGNAL_B2B_INQUIRY' ? s.params?.aanvrager_id : null;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => openSignaal(s)}
                  className={`group w-full text-left flex items-center gap-3 rounded-lg border border-gray-200 border-l-4 ${stijl.rand} px-4 py-3 transition-colors hover:border-gray-300 hover:shadow-sm ${
                    s.isRead ? 'bg-gray-50' : 'bg-white'
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${s.isRead ? 'text-gray-400' : stijl.icon}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {!s.isRead && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stijl.stip}`} aria-label={t('opleiderDashboard.ongelezen')} />}
                      <span className={`text-sm truncate ${s.isRead ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>{titel}</span>
                    </div>
                    {tekst && <p className="text-sm text-gray-500 line-clamp-2">{tekst}</p>}
                    {waarschuwing && (
                      <p className="mt-1 text-xs font-medium text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                        {waarschuwing}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xs text-gray-400 whitespace-nowrap">{relatieveTijd(s.occurredAt)}</div>
                    {actie ? (
                      <div className="text-xs font-medium text-[#0B1F3A] hidden sm:block whitespace-nowrap">{actie}</div>
                    ) : (
                      <div className="text-xs text-gray-400 hidden sm:block">{t(`opleiderDashboard.categorie.${s.category}`)}</div>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#0B1F3A] flex-shrink-0" />
                </button>
                {chatMet && (
                  <button
                    type="button"
                    onClick={() => navigate(`/opleider/chats/zakelijk?contact=${encodeURIComponent(String(chatMet))}`)}
                    className="mt-1 ml-9 inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-[#0B1F3A] hover:bg-blue-50"
                  >
                    <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                    {t('opleiderDashboard.toelichtingsChat')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default OpleiderSignalenFeed;
